import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

interface MediaPreviewModalProps {
  media: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
}

export const MediaPreviewModal = ({
  media,
  open,
  onOpenChange,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
}: MediaPreviewModalProps) => {
  if (!media) return null;

  const imageUrl = supabase.storage
    .from('media-library')
    .getPublicUrl(media.storage_path).data.publicUrl;

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = media.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl h-[90vh] p-0 overflow-hidden">
        <div className="relative w-full h-full flex">
          {/* Navigation Buttons */}
          {hasPrevious && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-background/80 hover:bg-background"
              onClick={onPrevious}
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
          )}

          {hasNext && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-background/80 hover:bg-background"
              onClick={onNext}
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          )}

          {/* Close Button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4 z-10 bg-background/80 hover:bg-background"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-6 w-6" />
          </Button>

          {/* Image */}
          <div className="flex-1 flex items-center justify-center bg-muted p-8">
            <img
              src={imageUrl}
              alt={media.alt_text || media.filename}
              className="max-w-full max-h-full object-contain"
            />
          </div>

          {/* Sidebar */}
          <div className="w-80 bg-card p-6 overflow-y-auto border-l">
            <h3 className="font-semibold text-lg mb-4">{media.title || media.filename}</h3>
            
            {media.alt_text && (
              <div className="mb-4">
                <p className="text-sm text-muted-foreground mb-1">Alt Text</p>
                <p className="text-sm">{media.alt_text}</p>
              </div>
            )}

            <div className="space-y-3 text-sm">
              <div>
                <p className="text-muted-foreground">File Size</p>
                <p>{formatFileSize(media.file_size)}</p>
              </div>

              {media.width && media.height && (
                <div>
                  <p className="text-muted-foreground">Dimensions</p>
                  <p>{media.width} × {media.height} px</p>
                </div>
              )}

              <div>
                <p className="text-muted-foreground">Type</p>
                <p>{media.mime_type}</p>
              </div>

              <div>
                <p className="text-muted-foreground">Uploaded</p>
                <p>{new Date(media.created_at).toLocaleDateString()}</p>
              </div>
            </div>

            <div className="mt-6 space-y-2">
              <Button
                variant="outline"
                className="w-full"
                onClick={handleDownload}
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  navigator.clipboard.writeText(imageUrl);
                }}
              >
                Copy URL
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
